import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../utils/api';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V3 = '#51158C';
const V4 = '#2B0057';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match');

    setLoading(true);
    try {
      const { data } = await API.post(`/auth/reset-password/${token}`, { password: form.password });
      setSuccess(true);
      toast.success('Password reset successful! 🎉');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px',
    background: 'var(--bg3)', border: '1.5px solid var(--border)', borderRadius: 10,
    color: 'var(--text)', fontSize: 15,
    fontFamily: "'Satoshi', 'DM Sans', sans-serif",
    transition: 'all 0.2s', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 16px 60px', background: 'var(--bg)', position: 'relative', overflow: 'hidden' }}>
      {/* Background accent */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
        <div style={{ position: 'absolute', bottom: '-200px', right: '-200px', width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${V4}44 0%, transparent 70%)` }} />
        <div style={{ position: 'absolute', top: '-200px', left: '-100px', width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${V1}22 0%, transparent 70%)` }} />
      </div>

      <div style={{ width: '100%', maxWidth: 440, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 32, color: 'var(--v1)', marginBottom: 8 }}>
            Thiran<span style={{ color: 'var(--text)' }}>X</span>
          </div>
          <h2 style={{ fontSize: 'clamp(24px,5vw,32px)', fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8, color: 'var(--text)' }}>
            {success ? 'Password Reset!' : 'Set New Password'}
          </h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
            {success ? 'Redirecting you to login...' : 'Enter your new password below'}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="card" style={{
            background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: 32,
            boxShadow: 'var(--shadow-md)'
          }}>
            {/* Key icon */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, margin: '0 auto',
                background: `linear-gradient(135deg,${V4},${V3})`,
                border: `1px solid ${V1}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B163FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
              </div>
            </div>

            <div className="form-group">
              <label style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>New Password</label>
              <input
                name="password" type="password" value={form.password} onChange={handleChange}
                placeholder="Min. 6 characters" required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = `${V1}77`}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            <div className="form-group">
              <label style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>Confirm Password</label>
              <input
                name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange}
                placeholder="Re-enter password" required
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = `${V1}77`}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>

            <button
              type="submit" disabled={loading}
              className="btn btn-primary"
              style={{
                width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px', marginTop: 12,
                fontWeight: 700, background: `linear-gradient(135deg,${V1},${V3})`,
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1, transition: 'all 0.2s'
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(127,0,255,0.3)'; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'none'; } }}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <p style={{ textAlign: 'center', marginTop: 24, color: 'var(--muted)', fontSize: 14 }}>
              <Link to="/login" style={{ color: 'var(--v1)', fontWeight: 700, textDecoration: 'none' }}>← Back to Login</Link>
            </p>
          </form>
        ) : (
          /* Success state */
          <div className="card" style={{
            background: 'var(--card-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: 40,
            boxShadow: 'var(--shadow-md)', textAlign: 'center'
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', margin: '0 auto 24px',
              background: 'linear-gradient(135deg, #059669, #10b981)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h3 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 800, marginBottom: 12 }}>All Done!</h3>
            <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
              Your password has been reset successfully. You'll be redirected to the login page shortly.
            </p>

            <Link to="/login" className="btn btn-primary" style={{
              display: 'inline-flex', justifyContent: 'center', fontSize: 15, padding: '14px 32px',
              fontWeight: 700, background: `linear-gradient(135deg,${V1},${V3})`,
              border: 'none', textDecoration: 'none',
            }}>
              Go to Login
            </Link>
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          <Link to="/" style={{ color: 'var(--v1)', textDecoration: 'none', fontWeight: 600 }}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
